import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { flexRender, getCoreRowModel, useReactTable, type ColumnDef, type ColumnSizingState, type Header } from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, arrayMove, horizontalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { FilterClause } from '../api/client';
import type { FieldMeta, ResourceMeta } from '../meta/types';
import { Button, IconButton } from '../design/primitives/Button';
import { Checkbox } from '../design/primitives/Input';
import { ContextMenu, DropdownMenu, type MenuItemDef } from '../design/primitives/Overlay';
import { EmptyState, ErrorBanner, Skeleton } from '../design/primitives/Misc';
import { Icon, type IconName } from '../design/icons';
import { FieldEditor, renderField, type Widgets } from '../fields/registry';
import { useIsPhone } from '../design/useMediaQuery';
import { FilterPopover } from './FilterBar';
import { downloadText, toCsv } from './csv';

// DataGrid — headless @tanstack/react-table for column state (order/visibility/sizing/
// sorting/selection) + @tanstack/react-virtual for rows. Server-driven sort/filter via
// callbacks; group-by and CSV over loaded rows; inline edit through the field registry.

export interface GridColumn<T = Record<string, unknown>> {
  id: string;
  header: ReactNode;
  field?: FieldMeta;
  accessor?: (row: T) => unknown;
  cell?: (row: T) => ReactNode;
  width?: number;
  minWidth?: number;
  align?: 'left' | 'right' | 'center';
  sortable?: boolean;
  sortKey?: string;
  editable?: boolean;
  hidden?: boolean;
}

export interface ColumnState {
  order?: string[];
  hidden?: string[];
  sizes?: Record<string, number>;
}

export interface SortState {
  id: string;
  desc: boolean;
}

export type Density = 'compact' | 'comfortable' | 'spacious';

export interface BulkActionDef {
  name: string;
  label: string;
  icon?: IconName;
  tone?: 'default' | 'primary' | 'danger';
  run: (ids: string[]) => void | Promise<void>;
}

export interface DataGridProps<T = Record<string, unknown>> {
  columns: GridColumn<T>[];
  rows: T[];
  keyOf: (row: T) => string;
  meta?: ResourceMeta;
  loading?: boolean;
  /** Rows are on screen but a newer answer is on its way (a filter, sort or search changed). */
  refreshing?: boolean;
  error?: unknown;
  empty?: ReactNode;
  hasMore?: boolean;
  loadMore?: () => void;
  loadingMore?: boolean;
  sort?: SortState[];
  onSortChange?: (sort: SortState[]) => void;
  onFilterAdd?: (clause: FilterClause) => void;
  selection?: Set<string>;
  onSelectionChange?: (next: Set<string>) => void;
  onRowClick?: (row: T) => void;
  activeRowId?: string | null;
  onCellEdit?: (row: T, field: FieldMeta, value: unknown, previous: unknown) => void | Promise<void>;
  density?: Density;
  onDensityChange?: (d: Density) => void;
  groupBy?: string | null;
  onGroupByChange?: (col: string | null) => void;
  columnState?: ColumnState;
  onColumnStateChange?: (s: ColumnState) => void;
  bulkActions?: BulkActionDef[];
  /** Builds the right-click / row "⋯" menu for a row. `selectedIds` is the effective
   *  selection (the row itself when it isn't part of a multi-selection). */
  rowMenu?: (row: T, selectedIds: string[]) => MenuItemDef[];
  exportName?: string;
  /** Height of the scroll area. Default: fill available (min 240px). */
  height?: number | string;
  toolbarLeft?: ReactNode;
  tools?: ReactNode;
  hideToolbar?: boolean;
  widgets?: Widgets;
  /** Row id → extra class (e.g. 'muted'). */
  rowClass?: (row: T) => string | undefined;
  'aria-label'?: string;
}

const ROW_H: Record<Density, number> = { compact: 28, comfortable: 36, spacious: 46 };

type Item<T> = { kind: 'row'; row: T; key: string; index: number } | { kind: 'group'; key: string; label: ReactNode; count: number; collapsed: boolean };

function SortableHeader<T>({ header, children, onResizeEnd }: { header: Header<T, unknown>; children: ReactNode; onResizeEnd: () => void }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: header.column.id });
  const col = header.column.columnDef.meta as { col: GridColumn<T> } | undefined;
  return (
    <div
      ref={setNodeRef}
      role="columnheader"
      aria-sort={header.column.getIsSorted() ? (header.column.getIsSorted() === 'desc' ? 'descending' : 'ascending') : undefined}
      className={['hf-grid-th', col?.col.align === 'right' && 'right', isDragging && 'dragging'].filter(Boolean).join(' ')}
      style={{ transform: CSS.Translate.toString(transform), transition, zIndex: isDragging ? 2 : undefined }}
    >
      <span className="hf-grid-th-grip" {...attributes} {...listeners} aria-label="Reorder column" title="Drag to reorder">
        <Icon name="grip-vertical" size={12} />
      </span>
      {children}
      <span
        className={['hf-grid-resize', header.column.getIsResizing() && 'active'].filter(Boolean).join(' ')}
        onMouseDown={header.getResizeHandler()}
        onTouchStart={header.getResizeHandler()}
        onMouseUp={onResizeEnd}
        onDoubleClick={() => header.column.resetSize()}
        role="separator"
        aria-orientation="vertical"
        aria-label={`Resize ${String(header.column.columnDef.header)}`}
      />
    </div>
  );
}

export function DataGrid<T = Record<string, unknown>>(props: DataGridProps<T>) {
  const {
    columns,
    rows,
    keyOf,
    loading,
    refreshing,
    error,
    empty,
    hasMore,
    loadMore,
    loadingMore,
    sort = [],
    onSortChange,
    onFilterAdd,
    selection,
    onSelectionChange,
    onRowClick,
    activeRowId,
    onCellEdit,
    density: densityProp,
    onDensityChange,
    groupBy: groupByProp,
    onGroupByChange,
    columnState,
    onColumnStateChange,
    bulkActions = [],
    rowMenu,
    exportName = 'export',
    height,
    toolbarLeft,
    tools,
    hideToolbar,
    widgets,
    rowClass,
  } = props;

  // ── Local-or-controlled state ────────────────────────────────────────────
  const [densityLocal, setDensityLocal] = useState<Density>('comfortable');
  const density = densityProp ?? densityLocal;
  const setDensity = (d: Density) => (onDensityChange ? onDensityChange(d) : setDensityLocal(d));
  const [groupLocal, setGroupLocal] = useState<string | null>(null);
  const groupBy = groupByProp !== undefined ? groupByProp : groupLocal;
  const setGroupBy = (g: string | null) => (onGroupByChange ? onGroupByChange(g) : setGroupLocal(g));
  const [colLocal, setColLocal] = useState<ColumnState>({});
  const cs = columnState ?? colLocal;
  const setCs = useCallback(
    (patch: Partial<ColumnState>) => {
      const next = { ...cs, ...patch };
      if (onColumnStateChange) onColumnStateChange(next);
      else setColLocal(next);
    },
    [cs, onColumnStateChange],
  );
  const [selLocal, setSelLocal] = useState<Set<string>>(new Set());
  const selected = selection ?? selLocal;
  const setSelected = (s: Set<string>) => (onSelectionChange ? onSelectionChange(s) : setSelLocal(s));
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [active, setActive] = useState<{ row: number; col: number } | null>(null);
  const [editing, setEditing] = useState<{ key: string; col: string } | null>(null);
  const [filterFor, setFilterFor] = useState<{ col: GridColumn<T>; el: HTMLElement } | null>(null);
  const [ctxMenu, setCtxMenu] = useState<{ items: MenuItemDef[]; x: number; y: number } | null>(null);

  // ── react-table ──────────────────────────────────────────────────────────
  const tableColumns = useMemo<ColumnDef<T, unknown>[]>(
    () =>
      columns.map((c) => ({
        id: c.id,
        accessorFn: (row: T) => (c.accessor ? c.accessor(row) : (row as Record<string, unknown>)[c.id]),
        header: () => c.header,
        size: c.width ?? (c.field?.width ?? defaultWidth(c.field)),
        minSize: c.minWidth ?? 56,
        enableSorting: c.sortable ?? c.field?.sortable ?? false,
        meta: { col: c },
      })),
    [columns],
  );
  const columnVisibility = useMemo(() => {
    const v: Record<string, boolean> = {};
    for (const c of columns) v[c.id] = !(cs.hidden ? cs.hidden.includes(c.id) : c.hidden);
    return v;
  }, [columns, cs.hidden]);
  const columnOrder = useMemo(() => {
    const ids = columns.map((c) => c.id);
    const o = (cs.order ?? []).filter((id) => ids.includes(id));
    return [...o, ...ids.filter((id) => !o.includes(id))];
  }, [columns, cs.order]);
  const [sizing, setSizing] = useState<ColumnSizingState>(cs.sizes ?? {});
  useEffect(() => {
    if (cs.sizes) setSizing(cs.sizes);
  }, [cs.sizes]);

  const table = useReactTable<T>({
    data: rows,
    columns: tableColumns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => keyOf(row),
    state: { columnVisibility, columnOrder, columnSizing: sizing, sorting: sort },
    onColumnSizingChange: (u) => setSizing((old) => (typeof u === 'function' ? u(old) : u)),
    columnResizeMode: 'onChange',
    manualSorting: true,
    enableColumnResizing: true,
  });
  const leafHeaders = table.getHeaderGroups()[0]?.headers ?? [];
  const visibleCols = leafHeaders.map((h) => (h.column.columnDef.meta as { col: GridColumn<T> }).col);

  const hasSelect = Boolean(onSelectionChange) || bulkActions.length > 0;
  const template = useMemo(() => {
    const parts = leafHeaders.map((h, i) => (i === leafHeaders.length - 1 ? `minmax(${h.getSize()}px, 1fr)` : `${h.getSize()}px`));
    return `${hasSelect ? '36px ' : ''}${parts.join(' ')}`;
  }, [leafHeaders, hasSelect]);

  // ── Grouping → flattened items ───────────────────────────────────────────
  const groupCol = groupBy ? columns.find((c) => c.id === groupBy) : undefined;
  const items = useMemo<Item<T>[]>(() => {
    if (!groupCol) return rows.map((row, index) => ({ kind: 'row', row, key: keyOf(row), index }));
    const val = (r: T) => (groupCol.accessor ? groupCol.accessor(r) : (r as Record<string, unknown>)[groupCol.id]);
    const buckets = new Map<string, T[]>();
    for (const r of rows) {
      const k = String(val(r) ?? '—');
      if (!buckets.has(k)) buckets.set(k, []);
      buckets.get(k)!.push(r);
    }
    const out: Item<T>[] = [];
    let index = 0;
    for (const [k, list] of buckets) {
      const isCollapsed = collapsed.has(k);
      const first = list[0]!;
      const label = groupCol.field ? renderField(groupCol.field, val(first), first as Record<string, unknown>, { compact: true, widgets }) : k;
      out.push({ kind: 'group', key: `g:${k}`, label, count: list.length, collapsed: isCollapsed });
      if (!isCollapsed) for (const row of list) out.push({ kind: 'row', row, key: keyOf(row), index: index++ });
    }
    return out;
  }, [rows, groupCol, collapsed, keyOf, widgets]);
  const rowItems = useMemo(() => items.filter((i): i is Extract<Item<T>, { kind: 'row' }> => i.kind === 'row'), [items]);

  // ── Virtualization ───────────────────────────────────────────────────────
  const scrollRef = useRef<HTMLDivElement>(null);
  const rowH = ROW_H[density];
  // Phones get one stacked card per row instead of a table: at 390px a six-column grid can
  // only ever show two of them. Same data, same handlers, same virtualiser — the row simply
  // lays itself out vertically (see .hf-grid.as-cards in engine.css) and, because a card's
  // height depends on how many fields it carries, we stop pinning the row height and let
  // measureElement report the real one.
  const isPhone = useIsPhone();
  const CARD_ESTIMATE = 96;
  const virt = useVirtualizer({
    count: items.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: (i) => (items[i]?.kind === 'group' ? 30 : isPhone ? CARD_ESTIMATE : rowH),
    overscan: 12,
    getItemKey: (i) => items[i]?.key ?? i,
    // A container that measures 0 (hidden tab, jsdom) still renders a page of rows.
    observeElementRect: (instance, cb) => {
      const el = instance.scrollElement;
      if (!el) return;
      const measure = () => {
        const r = el.getBoundingClientRect();
        cb(r.height > 0 ? { width: r.width, height: r.height } : { width: 1200, height: 600 });
      };
      measure();
      const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
      ro?.observe(el);
      return () => ro?.disconnect();
    },
  });
  useEffect(() => virt.measure(), [rowH, isPhone, virt]);

  // Infinite scroll: load the next page when the last row comes into view.
  useEffect(() => {
    if (!hasMore || loadingMore || !loadMore) return;
    const last = virt.getVirtualItems().at(-1);
    if (last && last.index >= items.length - 3) loadMore();
  }, [virt.getVirtualItems(), hasMore, loadingMore, loadMore, items.length, virt]);

  // ── Selection ────────────────────────────────────────────────────────────
  const allKeys = useMemo(() => rows.map(keyOf), [rows, keyOf]);
  const allSelected = allKeys.length > 0 && allKeys.every((k) => selected.has(k));
  const someSelected = !allSelected && allKeys.some((k) => selected.has(k));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(allKeys));
  const toggle = (k: string) => {
    const n = new Set(selected);
    if (n.has(k)) n.delete(k);
    else n.add(k);
    setSelected(n);
  };

  // Open the row menu (right-click / "⋯" / Menu key). The effective selection is the current
  // multi-selection when the row is part of it, else just this row.
  const openRowMenu = useCallback(
    (row: T, x: number, y: number) => {
      if (!rowMenu) return;
      const key = keyOf(row);
      const ids = selected.has(key) && selected.size > 1 ? [...selected] : [key];
      const items = rowMenu(row, ids);
      if (items.length) setCtxMenu({ items, x, y });
    },
    [rowMenu, keyOf, selected],
  );

  // ── Keyboard ─────────────────────────────────────────────────────────────
  const focusRow = (i: number) => {
    const clamped = Math.max(0, Math.min(i, rowItems.length - 1));
    setActive((a) => ({ row: clamped, col: a?.col ?? 0 }));
    const itemIndex = items.findIndex((it) => it.kind === 'row' && it.index === clamped);
    if (itemIndex >= 0) virt.scrollToIndex(itemIndex, { align: 'auto' });
  };
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (editing) return;
    const t = e.target as HTMLElement;
    if (t.tagName === 'INPUT' || t.tagName === 'SELECT' || t.tagName === 'TEXTAREA' || t.isContentEditable) return;
    const cur = active?.row ?? -1;
    switch (e.key) {
      case 'ArrowDown':
      case 'j':
        e.preventDefault();
        focusRow(cur + 1);
        break;
      case 'ArrowUp':
      case 'k':
        e.preventDefault();
        focusRow(cur - 1);
        break;
      case 'ArrowRight':
        e.preventDefault();
        setActive((a) => ({ row: a?.row ?? 0, col: Math.min((a?.col ?? 0) + 1, visibleCols.length - 1) }));
        break;
      case 'ArrowLeft':
        e.preventDefault();
        setActive((a) => ({ row: a?.row ?? 0, col: Math.max((a?.col ?? 0) - 1, 0) }));
        break;
      case 'Home':
        e.preventDefault();
        focusRow(0);
        break;
      case 'End':
        e.preventDefault();
        focusRow(rowItems.length - 1);
        break;
      case 'Enter': {
        const it = rowItems[cur];
        if (it && onRowClick) {
          e.preventDefault();
          onRowClick(it.row);
        }
        break;
      }
      case ' ':
      case 'x': {
        const it = rowItems[cur];
        if (it && hasSelect) {
          e.preventDefault();
          toggle(it.key);
        }
        break;
      }
      case 'e':
      case 'F2': {
        const it = rowItems[cur];
        const col = visibleCols[active?.col ?? 0];
        if (it && col && canEdit(col)) {
          e.preventDefault();
          setEditing({ key: it.key, col: col.id });
        }
        break;
      }
      case 'a':
        if ((e.metaKey || e.ctrlKey) && hasSelect) {
          e.preventDefault();
          setSelected(new Set(allKeys));
        }
        break;
      case 'ContextMenu':
      case 'F10': {
        if (e.key === 'F10' && !e.shiftKey) break;
        const it = rowItems[cur];
        if (it && rowMenu) {
          e.preventDefault();
          const el = scrollRef.current?.querySelector<HTMLElement>(`[aria-rowindex="${it.index + 1}"] .hf-grid-rowmenu`) ?? scrollRef.current?.querySelector<HTMLElement>(`[aria-rowindex="${it.index + 1}"]`);
          const r = el?.getBoundingClientRect();
          openRowMenu(it.row, r ? r.left : 200, r ? r.bottom : 200);
        }
        break;
      }
      case 'Escape':
        if (ctxMenu) setCtxMenu(null);
        else if (selected.size) setSelected(new Set());
        break;
    }
  };
  const canEdit = (c: GridColumn<T>) => Boolean(onCellEdit) && c.editable !== false && Boolean(c.field && !c.field.readOnly && c.field.inUpdate);

  // ── Column reorder (dnd-kit) ─────────────────────────────────────────────
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const onDragEnd = (ev: DragEndEvent) => {
    const { active: a, over } = ev;
    if (!over || a.id === over.id) return;
    const from = columnOrder.indexOf(String(a.id));
    const to = columnOrder.indexOf(String(over.id));
    setCs({ order: arrayMove(columnOrder, from, to) });
  };

  // ── Header menu ──────────────────────────────────────────────────────────
  const headerMenu = (h: Header<T, unknown>, col: GridColumn<T>, el: HTMLElement | null): MenuItemDef[] => {
    const sortable = h.column.getCanSort() && onSortChange;
    const key = col.sortKey ?? col.id;
    return [
      ...(sortable
        ? [
            { label: 'Sort ascending', icon: 'arrow-up' as IconName, onSelect: () => onSortChange([{ id: key, desc: false }]) },
            { label: 'Sort descending', icon: 'arrow-down' as IconName, onSelect: () => onSortChange([{ id: key, desc: true }]) },
            { separator: true, label: '' },
          ]
        : []),
      ...(col.field?.filterable && onFilterAdd ? [{ label: 'Filter…', icon: 'filter' as IconName, onSelect: () => el && setFilterFor({ col, el }) }] : []),
      ...(col.field && ['enum', 'relation', 'boolean', 'text', 'date'].includes(col.field.type) ? [{ label: groupBy === col.id ? 'Ungroup' : 'Group by', icon: 'rows' as IconName, onSelect: () => setGroupBy(groupBy === col.id ? null : col.id) }] : []),
      { separator: true, label: '' },
      { label: 'Hide column', icon: 'eye-off' as IconName, onSelect: () => setCs({ hidden: [...(cs.hidden ?? columns.filter((c) => c.hidden).map((c) => c.id)), col.id] }) },
    ];
  };

  // ── Export ───────────────────────────────────────────────────────────────
  const exportCsv = () => {
    const cols = visibleCols.map((c) => ({
      header: typeof c.header === 'string' ? c.header : c.field?.label ?? c.id,
      value: (r: T) => (c.accessor ? c.accessor(r) : (r as Record<string, unknown>)[c.id]),
    }));
    downloadText(`${exportName}.csv`, toCsv(cols, rows));
  };

  const columnMenu: MenuItemDef[] = [
    { heading: true, label: 'Columns' },
    ...columns.map((c) => ({
      key: c.id,
      label: typeof c.header === 'string' ? c.header : c.field?.label ?? c.id,
      checked: columnVisibility[c.id] !== false,
      onSelect: () => {
        const hidden = new Set(cs.hidden ?? columns.filter((x) => x.hidden).map((x) => x.id));
        if (hidden.has(c.id)) hidden.delete(c.id);
        else hidden.add(c.id);
        setCs({ hidden: [...hidden] });
      },
    })),
    { separator: true, label: '' },
    { label: 'Reset columns', icon: 'rotate-ccw', onSelect: () => setCs({ hidden: undefined, order: undefined, sizes: undefined }) },
  ];
  const densityMenu: MenuItemDef[] = (['compact', 'comfortable', 'spacious'] as Density[]).map((d) => ({ label: d[0]!.toUpperCase() + d.slice(1), checked: density === d, onSelect: () => setDensity(d) }));
  // Cards hide the header row, and the header is where sorting lives — so on a phone the
  // toolbar carries it instead. Same onSortChange, so the URL and saved views round-trip.
  const sortMenu: MenuItemDef[] = onSortChange
    ? columns
        .filter((c) => c.sortable !== false && !c.hidden)
        .flatMap((c) => {
          const key = c.sortKey ?? c.id;
          const cur = sort.find((x) => x.id === key);
          const label = c.field?.label ?? (typeof c.header === 'string' ? c.header : c.id);
          return [
            { key: `${key}-asc`, label: `${label} ↑`, checked: !!cur && !cur.desc, onSelect: () => onSortChange([{ id: key, desc: false }]) },
            { key: `${key}-desc`, label: `${label} ↓`, checked: !!cur && cur.desc, onSelect: () => onSortChange([{ id: key, desc: true }]) },
          ];
        })
    : [];
  const groupMenu: MenuItemDef[] = [
    { label: 'No grouping', checked: !groupBy, onSelect: () => setGroupBy(null) },
    { separator: true, label: '' },
    ...columns.filter((c) => c.field && ['enum', 'relation', 'boolean', 'text', 'date'].includes(c.field.type)).map((c) => ({ label: c.field!.label, checked: groupBy === c.id, onSelect: () => setGroupBy(c.id) })),
  ];

  const selectedIds = [...selected];
  const scrollStyle = height != null ? { height } : undefined;

  return (
    <div className={['hf-grid', `density-${density}`, isPhone && 'as-cards', refreshing && 'is-refreshing'].filter(Boolean).join(' ')} aria-busy={loading || refreshing || undefined}>
      {!hideToolbar && (
        <div className="hf-grid-tools">
          <div className="hf-row hf-grow" style={{ flexWrap: 'wrap' }}>
            {toolbarLeft}
          </div>
          <div className="hf-row" style={{ gap: 2 }}>
            {refreshing && (
              <span className="hf-grid-updating" role="status">
                <span className="hf-spinner" aria-hidden />
                Updating…
              </span>
            )}
            {tools}
            {isPhone && sortMenu.length > 0 && (
              <DropdownMenu trigger={<IconButton icon="arrow-up-down" label="Sort" size="sm" />} items={sortMenu} aria-label="Sort" />
            )}
            <DropdownMenu trigger={<IconButton icon="rows" label="Group by" size="sm" />} items={groupMenu} aria-label="Group by" />
            <DropdownMenu trigger={<IconButton icon="align-left" label="Density" size="sm" />} items={densityMenu} aria-label="Density" />
            <DropdownMenu trigger={<IconButton icon="columns" label="Columns" size="sm" />} items={columnMenu} aria-label="Columns" />
            <IconButton icon="download" label="Export CSV" size="sm" onClick={exportCsv} disabled={rows.length === 0} />
          </div>
        </div>
      )}

      <div ref={scrollRef} className="hf-grid-scroll hf-scroll" style={scrollStyle} role="grid" aria-label={props['aria-label']} aria-rowcount={rows.length} aria-busy={loading || undefined} tabIndex={0} onKeyDown={onKey}>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <div className="hf-grid-head" role="row" style={{ gridTemplateColumns: template }}>
            {hasSelect && (
              <div className="hf-grid-th sel" role="columnheader">
                <Checkbox aria-label="Select all" checked={allSelected} indeterminate={someSelected} onChange={toggleAll} />
              </div>
            )}
            <SortableContext items={columnOrder} strategy={horizontalListSortingStrategy}>
              {leafHeaders.map((h) => {
                const col = (h.column.columnDef.meta as { col: GridColumn<T> }).col;
                const sorted = sort.find((s) => s.id === (col.sortKey ?? col.id));
                const sortable = h.column.getCanSort() && onSortChange;
                return (
                  <SortableHeader key={h.id} header={h} onResizeEnd={() => setCs({ sizes: table.getState().columnSizing })}>
                    <button
                      type="button"
                      className={['hf-grid-th-label', sortable && 'sortable'].filter(Boolean).join(' ')}
                      disabled={!sortable}
                      onClick={() => sortable && onSortChange([{ id: col.sortKey ?? col.id, desc: sorted ? !sorted.desc : false }])}
                      title={sortable ? 'Sort' : undefined}
                    >
                      <span className="hf-cell-trunc">{flexRender(h.column.columnDef.header, h.getContext())}</span>
                      {sorted && <Icon name={sorted.desc ? 'arrow-down' : 'arrow-up'} size={12} className="sort" />}
                    </button>
                    <DropdownMenu
                      aria-label={`${col.field?.label ?? col.id} options`}
                      trigger={<IconButton icon="chevron-down" label="Column options" size="sm" className="hf-grid-th-menu" />}
                      items={headerMenu(h, col, scrollRef.current)}
                    />
                  </SortableHeader>
                );
              })}
            </SortableContext>
          </div>
        </DndContext>

        {error ? (
          <div style={{ padding: 'var(--s-4)' }}>
            <ErrorBanner error={error} />
          </div>
        ) : loading && rows.length === 0 ? (
          <div className="hf-grid-skeleton" aria-hidden>
            {Array.from({ length: 8 }, (_, i) => (
              <div key={i} className="hf-grid-tr" style={{ gridTemplateColumns: template, height: rowH }}>
                {hasSelect && <div className="hf-grid-td" />}
                {visibleCols.map((c) => (
                  <div key={c.id} className="hf-grid-td">
                    <Skeleton width={`${40 + ((i * 17 + c.id.length * 7) % 50)}%`} />
                  </div>
                ))}
              </div>
            ))}
          </div>
        ) : rows.length === 0 ? (
          <div>{empty ?? <EmptyState title="Nothing here yet" />}</div>
        ) : (
          <div className="hf-grid-body" style={{ height: virt.getTotalSize() }}>
            {virt.getVirtualItems().map((vi) => {
              const it = items[vi.index]!;
              const style = isPhone
                ? { transform: `translateY(${vi.start}px)`, gridTemplateColumns: '1fr' }
                : { transform: `translateY(${vi.start}px)`, height: vi.size, gridTemplateColumns: template };
              if (it.kind === 'group') {
                return (
                  <div key={it.key} className="hf-grid-group" role="row" style={{ ...style, gridTemplateColumns: '1fr' }} data-index={vi.index} ref={virt.measureElement}>
                    <button
                      type="button"
                      className="hf-grid-group-btn"
                      aria-expanded={!it.collapsed}
                      onClick={() =>
                        setCollapsed((s) => {
                          const n = new Set(s);
                          const k = it.key.slice(2);
                          if (n.has(k)) n.delete(k);
                          else n.add(k);
                          return n;
                        })
                      }
                    >
                      <Icon name={it.collapsed ? 'chevron-right' : 'chevron-down'} size={13} />
                      <span>{it.label}</span>
                      <span className="hf-muted hf-num">{it.count}</span>
                    </button>
                  </div>
                );
              }
              const { row, key, index } = it;
              const isActive = active?.row === index || activeRowId === key;
              const isSel = selected.has(key);
              return (
                <div
                  key={key}
                  role="row"
                  aria-selected={hasSelect ? isSel : undefined}
                  aria-rowindex={index + 1}
                  className={['hf-grid-tr', isActive && 'active', isSel && 'selected', onRowClick && 'clickable', rowClass?.(row)].filter(Boolean).join(' ')}
                  style={style}
                  data-index={vi.index}
                  ref={virt.measureElement}
                  onClick={() => {
                    setActive({ row: index, col: active?.col ?? 0 });
                    onRowClick?.(row);
                  }}
                  onContextMenu={
                    rowMenu
                      ? (e) => {
                          e.preventDefault();
                          setActive({ row: index, col: active?.col ?? 0 });
                          openRowMenu(row, e.clientX, e.clientY);
                        }
                      : undefined
                  }
                >
                  {hasSelect && (
                    <div className="hf-grid-td sel" role="gridcell" onClick={(e) => e.stopPropagation()}>
                      <Checkbox aria-label={`Select row ${index + 1}`} checked={isSel} onChange={() => toggle(key)} />
                    </div>
                  )}
                  {visibleCols.map((c, ci) => {
                    const value = c.accessor ? c.accessor(row) : (row as Record<string, unknown>)[c.id];
                    const isEditing = editing?.key === key && editing.col === c.id;
                    const editable = canEdit(c);
                    return (
                      <div
                        key={c.id}
                        role="gridcell"
                        data-label={c.field?.label ?? (typeof c.header === 'string' ? c.header : c.id)}
                        className={['hf-grid-td', ci === 0 && 'primary', c.align === 'right' || c.field?.align === 'right' ? 'right' : '', editable && 'editable', isEditing && 'editing', isActive && active?.col === ci && 'cell-active'].filter(Boolean).join(' ')}
                        onDoubleClick={(e) => {
                          if (!editable) return;
                          e.stopPropagation();
                          setEditing({ key, col: c.id });
                        }}
                        onClick={(e) => {
                          if (isEditing) e.stopPropagation();
                        }}
                      >
                        {isEditing && c.field ? (
                          <CellEditor
                            field={c.field}
                            value={value}
                            row={row as Record<string, unknown>}
                            widgets={widgets}
                            onDone={async (next) => {
                              setEditing(null);
                              if (next !== undefined && next !== value) await onCellEdit?.(row, c.field!, next, value);
                              scrollRef.current?.focus();
                            }}
                          />
                        ) : c.cell ? (
                          c.cell(row)
                        ) : c.field ? (
                          renderField(c.field, value, row as Record<string, unknown>, { compact: true, widgets })
                        ) : (
                          String(value ?? '')
                        )}
                      </div>
                    );
                  })}
                  {rowMenu && (
                    <IconButton
                      icon="more-horizontal"
                      label={`Actions for row ${index + 1}`}
                      size="sm"
                      className="hf-grid-rowmenu"
                      onClick={(e) => {
                        e.stopPropagation();
                        const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
                        openRowMenu(row, r.left, r.bottom);
                      }}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {(rows.length > 0 || hasMore) && (
        <div className="hf-grid-foot">
          <span className="hf-num">
            {rows.length} loaded{hasMore ? ' · more available' : ''}
            {selected.size ? ` · ${selected.size} selected` : ''}
          </span>
          {hasMore && (
            <Button size="sm" variant="ghost" onClick={loadMore} busy={loadingMore}>
              Load more
            </Button>
          )}
        </div>
      )}

      {selected.size > 0 && (bulkActions.length > 0 || onSelectionChange) && (
        <div className="hf-bulkbar" role="toolbar" aria-label="Bulk actions">
          <span className="hf-num">
            <b>{selected.size}</b> selected
          </span>
          <span className="hf-rule vertical" style={{ height: 16 }} />
          {bulkActions.map((a) => (
            <Button key={a.name} size="sm" variant={a.tone === 'danger' ? 'danger' : a.tone === 'primary' ? 'primary' : 'ghost'} icon={a.icon} onClick={() => void a.run(selectedIds)}>
              {a.label}
            </Button>
          ))}
          <Button size="sm" variant="ghost" icon="x" onClick={() => setSelected(new Set())} style={{ marginLeft: 'auto' }}>
            Clear
          </Button>
        </div>
      )}

      {ctxMenu && <ContextMenu open x={ctxMenu.x} y={ctxMenu.y} items={ctxMenu.items} onClose={() => setCtxMenu(null)} aria-label="Row actions" />}

      {filterFor && onFilterAdd && filterFor.col.field && (
        <FilterPopover
          anchor={filterFor.el}
          field={filterFor.col.field}
          onApply={(c) => {
            onFilterAdd(c);
            setFilterFor(null);
          }}
          onClose={() => setFilterFor(null)}
        />
      )}
    </div>
  );
}

function CellEditor({ field, value, row, widgets, onDone }: { field: FieldMeta; value: unknown; row: Record<string, unknown>; widgets?: Widgets; onDone: (next: unknown | undefined) => void }) {
  const [draft, setDraft] = useState<unknown>(value);
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const done = useRef(false);
  const commit = () => {
    if (done.current) return;
    done.current = true;
    onDone(draftRef.current);
  };
  const cancel = () => {
    if (done.current) return;
    done.current = true;
    onDone(undefined);
  };
  return (
    <div
      className="hf-grid-celledit"
      onBlur={(e) => {
        // Commit when focus leaves the editor entirely (not when moving within a popover it owns).
        const next = e.relatedTarget as HTMLElement | null;
        if (next && (e.currentTarget.contains(next) || next.closest('.hf-pop'))) return;
        window.setTimeout(commit, 0);
      }}
    >
      <FieldEditor field={field} value={draft} onChange={setDraft} row={row} autoFocus size="sm" widgets={widgets} onCommit={commit} onCancel={cancel} aria-label={`Edit ${field.label}`} />
    </div>
  );
}

function defaultWidth(f?: FieldMeta): number {
  switch (f?.type) {
    case 'boolean':
      return 90;
    case 'date':
      return 120;
    case 'datetime':
      return 160;
    case 'money':
    case 'number':
    case 'integer':
    case 'duration':
      return 120;
    case 'enum':
      return 130;
    case 'uuid':
      return 110;
    case 'textarea':
    case 'richtext':
      return 260;
    default:
      return 180;
  }
}
